import { FunctionComponent } from "react";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Spinner } from "../Spinner/Spinner";
import { Icon } from "../Icon/Icon";

interface FooterActionItemProps {
	icon: IconDefinition;
	label: string;
	subtitle?: string;
	onClick: () => void;
	isLoading?: boolean;
	loadingLabel?: string;
	/** When true, renders as a dropdown menu item instead of a top-level action */
	isMenuItem?: boolean;
	/** When true, renders with white-on-dark styling for the floating bulk action bar */
	dark?: boolean;
	/** When true, applies a primary (blue) background — only used in dark mode */
	primary?: boolean;
}

export const FooterActionItem: FunctionComponent<FooterActionItemProps> = ({
	icon,
	label,
	subtitle,
	onClick,
	isLoading,
	loadingLabel,
	isMenuItem,
	dark,
	primary,
}) => {
	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				<Spinner color={dark ? "#FFFFFF" : "#6B7280"} />
				<div className={`flex flex-row text-sm ml-1 ${dark ? "text-white" : "text-gray-700"}`}>
					{loadingLabel}
				</div>
			</div>
		);
	}

	if (isMenuItem) {
		return (
			<button
				className="w-full text-left px-3 py-2.5 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer duration-150 flex items-center gap-2"
				onClick={onClick}
			>
				<Icon className="w-4 text-gray-700" icon={icon} />
				<div className="flex flex-col">
					{label}
					{subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
				</div>
			</button>
		);
	}

	if (dark) {
		return (
			<button
				className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-colors ${
					primary
						? "bg-blue-action text-white hover:bg-blue-action-dark"
						: "text-white hover:bg-white-semi"
				}`}
				type="button"
				onClick={onClick}
			>
				<Icon className="w-3.5 h-3.5" icon={icon} />
				<span>{label}</span>
			</button>
		);
	}

	return (
		<div className="self-right items-center flex gap-1.5 cursor-pointer group" onClick={onClick}>
			<Icon className="w-5 text-gray-700 group-hover:text-brand-light" icon={icon} />
			<div className="flex flex-col text-sm text-gray-700 group-hover:text-brand-light">
				{label}
				{subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
			</div>
		</div>
	);
};
